import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { useBranch } from "@/contexts/BranchContext";
import { useAuth } from "@/contexts/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { fmtIDR, firstOfMonthISO, todayISO } from "@/lib/format";
import { toast } from "sonner";
import { FileSpreadsheet, Download, Receipt, Package, Wallet, RotateCcw, ShieldAlert } from "lucide-react";

const REPORTS = {
  "cashier-transactions": { label: "Transaksi Kasir", icon: Receipt, testid: "tab-cashier" },
  "sales-items": { label: "Penjualan Item", icon: Package, testid: "tab-sales" },
  "deposits": { label: "Deposit", icon: Wallet, testid: "tab-deposits" },
  "refunds": { label: "Refund Pasien", icon: RotateCcw, testid: "tab-refunds" },
};

function ReportPanel({ reportKey, branchId, dateFrom, dateTo }) {
  const [data, setData] = useState({ columns: [], rows: [], summary: {} });
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    setLoading(true);
    api.get(`/reports/${reportKey}`, { params: { branch_id: branchId, date_from: dateFrom, date_to: dateTo } })
      .then((r) => setData(r.data))
      .catch(() => toast.error("Gagal memuat laporan"))
      .finally(() => setLoading(false));
  }, [reportKey, branchId, dateFrom, dateTo]);

  const download = async () => {
    setDownloading(true);
    try {
      const res = await api.get(`/reports/${reportKey}`, {
        params: { branch_id: branchId, date_from: dateFrom, date_to: dateTo, export: 1 },
        responseType: "blob",
      });
      const cd = res.headers["content-disposition"] || "";
      const m = cd.match(/filename="?([^"]+)"?/);
      const filename = m ? m[1] : `${reportKey}.xlsx`;
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url; a.download = filename; document.body.appendChild(a); a.click();
      a.remove(); window.URL.revokeObjectURL(url);
      toast.success("File Excel berhasil diunduh");
    } catch {
      toast.error("Gagal mengunduh Excel");
    } finally {
      setDownloading(false);
    }
  };

  const { columns, rows, summary } = data;

  const summaryChips = useMemo(() => {
    const s = summary || {};
    if (reportKey === "cashier-transactions") return [["Total Transaksi", s.count ?? 0], ["Nilai Bersih", fmtIDR(s.total_amount || 0)], ["Dibatalkan", s.refund_count ?? 0]];
    if (reportKey === "sales-items") return [["Item Berbeda", s.item_count ?? 0], ["Qty Terjual", s.total_qty ?? 0], ["Total Penjualan", fmtIDR(s.total_val || 0)]];
    if (reportKey === "deposits") return [["Total Top Up", fmtIDR(s.total_topup || 0)], ["Total Redeem", fmtIDR(s.total_redeem || 0)], ["Jml Entri", s.count ?? 0]];
    if (reportKey === "refunds") return [["Jml Refund", s.count ?? 0], ["Total Nilai Refund", fmtIDR(s.total_amount || 0)]];
    return [];
  }, [reportKey, summary]);

  const isMoney = (col) => col.type === "money";

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap gap-2">
          {summaryChips.map(([k, v]) => (
            <div key={k} className="rounded-lg border bg-card px-3 py-1.5">
              <div className="text-[11px] uppercase tracking-wide text-muted-foreground">{k}</div>
              <div className="font-heading text-lg">{v}</div>
            </div>
          ))}
        </div>
        <Button onClick={download} disabled={downloading || loading} data-testid={`download-${reportKey}`}>
          <Download className="h-4 w-4 mr-1.5" /> {downloading ? "Menyiapkan..." : "Download Excel"}
        </Button>
      </div>

      <div className="overflow-x-auto rounded-lg border">
        <Table>
          <TableHeader>
            <TableRow>
              {columns.map((c) => (
                <TableHead key={c.key} className={isMoney(c) ? "text-right" : ""}>{c.label}</TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading && <TableRow><TableCell colSpan={columns.length || 1} className="text-center text-sm text-muted-foreground py-10">Memuat...</TableCell></TableRow>}
            {!loading && rows.length === 0 && <TableRow><TableCell colSpan={columns.length || 1} className="text-center text-sm text-muted-foreground py-10">Tidak ada data pada periode ini.</TableCell></TableRow>}
            {!loading && rows.map((row, i) => (
              <TableRow key={i} data-testid={`${reportKey}-row-${i}`}>
                {columns.map((c) => {
                  const v = row[c.key];
                  if (c.key === "status") {
                    return <TableCell key={c.key}><Badge className={`text-[10px] ${v === "Dibatalkan" ? "bg-rose-100 text-rose-800" : "bg-emerald-100 text-emerald-800"}`}>{v}</Badge></TableCell>;
                  }
                  if (c.key === "jenis") {
                    return <TableCell key={c.key}><Badge variant="outline" className={`text-[10px] ${v === "Top Up" ? "text-sky-700" : "text-amber-700"}`}>{v}</Badge></TableCell>;
                  }
                  return <TableCell key={c.key} className={`text-sm ${isMoney(c) ? "text-right tabular-nums" : ""}`}>{isMoney(c) ? fmtIDR(v || 0) : (v ?? "—")}</TableCell>;
                })}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

export default function Reports() {
  const { branchId } = useBranch();
  const { user: me } = useAuth();
  const canExportMR = me?.role === "owner" || me?.role === "super_admin";
  const [tab, setTab] = useState("cashier-transactions");
  const [dateFrom, setDateFrom] = useState(firstOfMonthISO());
  const [dateTo, setDateTo] = useState(todayISO());
  const [mrBusy, setMrBusy] = useState(false);

  const exportMedicalRecords = async () => {
    setMrBusy(true);
    try {
      const res = await api.get("/reports/medical-records-export", { params: { branch_id: branchId }, responseType: "blob" });
      const cd = res.headers["content-disposition"] || "";
      const m = cd.match(/filename="?([^"]+)"?/);
      const filename = m ? m[1] : "rekam_medis_lengkap.xlsx";
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url; a.download = filename; document.body.appendChild(a); a.click();
      a.remove(); window.URL.revokeObjectURL(url);
      toast.success("Export rekam medis berhasil diunduh");
    } catch {
      toast.error("Gagal mengunduh rekam medis");
    } finally {
      setMrBusy(false);
    }
  };


  const applyPreset = (kind) => {
    const now = new Date();
    if (kind === "today") { setDateFrom(todayISO()); setDateTo(todayISO()); }
    else if (kind === "7d") { const f = new Date(now); f.setDate(f.getDate() - 6); setDateFrom(f.toISOString().slice(0, 10)); setDateTo(todayISO()); }
    else if (kind === "this_month") { setDateFrom(firstOfMonthISO()); setDateTo(todayISO()); }
    else if (kind === "last_month") {
      const f = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      const l = new Date(now.getFullYear(), now.getMonth(), 0);
      setDateFrom(f.toISOString().slice(0, 10)); setDateTo(l.toISOString().slice(0, 10));
    }
  };

  return (
    <div className="space-y-6" data-testid="reports-page">
      <div>
        <h1 className="font-heading text-3xl tracking-tight flex items-center gap-2"><FileSpreadsheet className="h-7 w-7" /> Report</h1>
        <p className="mt-1 text-sm text-muted-foreground">Laporan operasional kasir. Data mengikuti cabang tempat Anda bertugas. Unduh ke Excel sesuai filter.</p>
      </div>

      {/* Emergency medical-record export (PMK 24/2022) — Owner/Super Admin only */}
      {canExportMR && (
        <Card className="border-amber-300 bg-amber-50/50" data-testid="mr-export-card">
          <CardContent className="p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div className="flex items-start gap-3">
              <span className="rounded-md bg-amber-500 p-2 text-white shrink-0"><ShieldAlert className="h-5 w-5" /></span>
              <div>
                <div className="font-heading text-lg leading-tight">Export Seluruh Rekam Medis</div>
                <p className="text-sm text-muted-foreground">Mitigasi Darurat PMK 24/2022 — unduh satu file Excel berisi semua riwayat medis pasien (Nama, NIK, Tanggal Kunjungan, Diagnosa ICD-10, Tindakan, Catatan Dokter). Mengikuti cabang aktif.</p>
              </div>
            </div>
            <Button onClick={exportMedicalRecords} disabled={mrBusy} data-testid="export-medical-records-btn" className="bg-amber-600 hover:bg-amber-700 shrink-0">
              <Download className="h-4 w-4 mr-1.5" /> {mrBusy ? "Menyiapkan..." : "Download Rekam Medis"}
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Global date filter */}
      <Card>
        <CardContent className="p-4 flex flex-col lg:flex-row lg:items-end gap-3">
          <div><Label className="text-xs">Dari</Label><Input type="date" data-testid="report-from" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} /></div>
          <div><Label className="text-xs">Sampai</Label><Input type="date" data-testid="report-to" value={dateTo} onChange={(e) => setDateTo(e.target.value)} /></div>
          <div className="flex gap-1.5 flex-wrap">
            <Button size="sm" variant="outline" onClick={() => applyPreset("today")}>Hari Ini</Button>
            <Button size="sm" variant="outline" onClick={() => applyPreset("7d")}>7 Hari</Button>
            <Button size="sm" variant="outline" onClick={() => applyPreset("this_month")}>Bulan Ini</Button>
            <Button size="sm" variant="outline" onClick={() => applyPreset("last_month")}>Bulan Lalu</Button>
          </div>
        </CardContent>
      </Card>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="flex flex-wrap h-auto">
          {Object.entries(REPORTS).map(([key, r]) => {
            const Icon = r.icon;
            return <TabsTrigger key={key} value={key} data-testid={r.testid} className="gap-1.5"><Icon className="h-4 w-4" /> {r.label}</TabsTrigger>;
          })}
        </TabsList>
        {Object.keys(REPORTS).map((key) => (
          <TabsContent key={key} value={key} className="mt-4">
            {tab === key && <ReportPanel reportKey={key} branchId={branchId} dateFrom={dateFrom} dateTo={dateTo} />}
          </TabsContent>
        ))}
      </Tabs>
    </div>
  );
}
